import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PaceTracker } from '../components/PaceTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, loading, authHeaders } = useAuth();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/applications/mine`, { headers: authHeaders(), withCredentials: true })
      .then((r) => setApps(r.data)).catch(() => {});
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]">Verifying credentials…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
        <div className="md:col-span-8">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Command Deck</div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Welcome,<br />
            <span className="font-display italic text-[#D4AF37]">{user.full_name.split(' ')[0]}.</span>
          </h1>
        </div>
        <div className="md:col-span-4 flex items-end">
          <div className="text-sm text-[#A3A3A3] font-light leading-relaxed">
            Signed in as <span className="text-[#F5F5F5]">{user.email}</span>
            <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">{user.role}</div>
          </div>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12 border-t border-b border-[#D4AF37]/15 py-10">
          {[
            ['Vault Listings', stats.total_properties],
            ['AUM Curated', `$${(stats.total_aum_usd / 1_000_000).toFixed(0)}M`],
            ['Valuations Run', stats.valuations_run],
            ['Flips Analyzed', stats.flips_analyzed ?? 0],
            ['Boardings', stats.applications_processed],
          ].map(([l, v]) => (
            <div key={l}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{l}</div>
              <div className="metric-number text-3xl md:text-4xl">{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sovereign Pace Tracker */}
      <PaceTracker />

      {/* Flip Command — Mercy's Primary Weapon */}
      <Link to="/flip-command" className="block sov-border bg-[#0A0A0A] p-8 md:p-10 mb-16 hover:border-[#D4AF37]/50 transition-colors group relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Primary Weapon · Active</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extralight leading-tight tracking-tight mb-4">
              Flip Command<br />
              <span className="font-display italic text-[#D4AF37]">ROI Intelligence.</span>
            </h2>
            <p className="text-sm text-[#A3A3A3] font-light leading-relaxed max-w-xl">
              The Refit Analyzer · Market Velocity Tracker · Renovation cadence and Shield-Score grading.
              Mercy's acquisition-and-exit weapon for high-velocity flips.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-center gap-6">
            <div className="text-right">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Engage</div>
              <div className="font-display text-3xl text-[#D4AF37] group-hover:translate-x-1 transition-transform">→</div>
            </div>
          </div>
        </div>
      </Link>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Your Boarding Applications</div>
          {apps.length === 0 ? (
            <div className="sov-border p-10 bg-[#0A0A0A]">
              <p className="text-base text-[#A3A3A3] font-light leading-relaxed mb-6">
                You have not yet submitted a boarding application. Begin your sovereign admission.
              </p>
              <Link to="/boarding" className="btn-sovereign">
                Begin Boarding <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {apps.map((a) => (
                <div key={a.id} className="sov-border p-6 bg-[#0A0A0A] flex items-center justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Ref · {a.id.slice(0, 8).toUpperCase()}</div>
                    <div className="text-sm tracking-wide">{a.target_region} · {a.investment_capacity}</div>
                  </div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">{a.status}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="md:col-span-5">
          <div className="sov-border p-8 bg-[#0A0A0A] mb-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Engage</div>
            <div className="space-y-3">
              <Link to="/flip-command" className="flex items-center justify-between border-b border-[#D4AF37]/15 py-3 hover:text-[#D4AF37] transition-colors">
                <span className="text-sm tracking-wide font-light">Run Flip Command</span>
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/vault" className="flex items-center justify-between border-b border-[#D4AF37]/15 py-3 hover:text-[#D4AF37] transition-colors">
                <span className="text-sm tracking-wide font-light">Enter The Vault</span>
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/valuation" className="flex items-center justify-between border-b border-[#D4AF37]/15 py-3 hover:text-[#D4AF37] transition-colors">
                <span className="text-sm tracking-wide font-light">Run Valuation Shield</span>
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/global-bridge" className="flex items-center justify-between py-3 hover:text-[#D4AF37] transition-colors">
                <span className="text-sm tracking-wide font-light">Cross The Bridge</span>
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
